// @flow
import { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useSendFocusEvent } from 'modules/focusManager';
import Tooltip from 'common/ui/Tooltip';
import { usePopupOptionsGetter } from 'hooks/useWindowsRects';
import { getPageType, PAGE_TYPES } from 'utils/pageTypes';
import type { PageTypes } from 'utils/pageTypes';
import { displayMessage } from './displayMessage';
import { Colors } from 'styles';
import type { GenericSnackBarProps } from 'common/GenericSnackBar/GenericSnackBar';
import { openWindow } from './openViewer';
import { MENU_WIDTH } from './constants';
import { useState, useEffect } from 'react';

export const logEvents = ({
  pageType,
  sendEvent,
  viewerIndex,
}: {
  pageType: PageTypes,
  sendEvent: $Call<typeof useSendFocusEvent>,
  viewerIndex: string,
}): void => {
  // Focus the other viewer if it is open. This way, both are easily accessible
  // to the viewer
  if (pageType === PAGE_TYPES.VIEWER) {
    sendEvent({ pageType, viewerId: viewerIndex });
  }

  // Timeout is necessary so that both events will fire
  setTimeout(() => {
    sendEvent({ pageType, viewerId: viewerIndex });
  }, 50);
};

type OnClickArguments = {
  to: string,
  openedViewerIds: Set<string>,
  isReporterOpen: boolean,
  isURTOpen: boolean,
  isWorklistOpen: boolean,
  setSnackbarMessage: ({
    message: React$Node,
    severity: GenericSnackBarProps['severity'],
  }) => void,
  sendEvent: $Call<typeof useSendFocusEvent>,
  viewerIndex: string,
  getPopupOptions: $Call<typeof usePopupOptionsGetter>,
};

export const onNavMenuItemClick = ({
  to,
  openedViewerIds,
  isReporterOpen,
  isURTOpen,
  isWorklistOpen,
  setSnackbarMessage,
  sendEvent,
  viewerIndex,
  getPopupOptions,
}: OnClickArguments) => {
  const pageType = getPageType(to);

  displayMessage({
    openedViewerIds,
    isReporterOpen,
    setSnackbarMessage,
    viewerIndex,
    pageType,
  });
  openWindow({
    to,
    openedViewerIds,
    isReporterOpen,
    isURTOpen,
    isWorklistOpen,
    viewerIndex,
    getPopupOptions,
    pageType,
  });

  logEvents({ pageType, sendEvent, viewerIndex });
};

type LinkStyleProps = {
  $selected: boolean,
  $isRelatedWindowOpen: boolean,
};

export const MenuItem = ({
  selected,
  isRelatedWindowOpen,
  to,
  viewerIndex,
  children,
  label,
  setSnackbarMessage,
  openedViewerIds,
  isReporterOpen,
  isURTOpen,
  isWorklistOpen,
  'data-testid': dataTestid,
  'aria-label': ariaLabel,
}: {
  selected: boolean,
  isRelatedWindowOpen: boolean,
  to: string,
  children: React$Node,
  label: string,
  setSnackbarMessage: ({
    message: React$Node,
    severity: GenericSnackBarProps['severity'],
  }) => void,
  viewerIndex: string,
  openedViewerIds: Set<string>,
  isReporterOpen: boolean,
  isURTOpen: boolean,
  isWorklistOpen: boolean,
  'data-value'?: string,
  'data-testid'?: string,
  'aria-label'?: string,
}): React$Node => {
  const sendEvent = useSendFocusEvent();
  const getPopupOptions = usePopupOptionsGetter();
  const [isLoading, setIsLoading] = useState(false);

  // If the reporter window has been opened but is still loading, we will
  // still have isReporterOpen equal to false, which will allow for multiple
  // reporter windows to be open. As a result, we use isLoading in conjunction with
  // isReporterOpen to identify whether we should be opening a new reporter window.
  // After 250ms, we set it back to false since isReporterOpen will now have the correct value.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => setIsLoading(false), 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Tooltip content={label} placement="right" backgroundColor={Colors.gray8} color={Colors.gray1}>
      <Link
        data-testid={dataTestid}
        data-analytics-name={`navbar-link-${dataTestid ?? label}`}
        data-page-open={isRelatedWindowOpen}
        aria-label={ariaLabel}
        to={to}
        onClick={(evt) => {
          evt.preventDefault();
          onNavMenuItemClick({
            to,
            openedViewerIds,
            isReporterOpen: isReporterOpen || isLoading,
            isURTOpen,
            isWorklistOpen,
            setSnackbarMessage,
            sendEvent,
            viewerIndex,
            getPopupOptions,
          });
          setIsLoading(true);
        }}
        $selected={selected}
        $isRelatedWindowOpen={isRelatedWindowOpen}
        css={css`
          app-region: no-drag;
          color: ${(props: LinkStyleProps) =>
            props.$isRelatedWindowOpen && !props.$selected ? Colors.mainBlue : Colors.white};
          width: ${MENU_WIDTH};
          height: 5.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: ${(props: LinkStyleProps) =>
            props.$selected ? Colors.mainBlue36Percent : 'transparent'};
          transition: 0.2s ease-in-out;
          flex-shrink: 0;
          position: relative;
          &:hover {
            background-color: ${Colors.mainBlue6Percent};
            color: ${(props: LinkStyleProps) =>
              props.$isRelatedWindowOpen && !props.$selected ? Colors.blue6 : Colors.white};
          }
          &::after {
            content: '';
            height: ${(props: LinkStyleProps) =>
              props.$isRelatedWindowOpen && !props.$selected ? '20%' : '0%'};
            opacity: ${(props: LinkStyleProps) =>
              props.$isRelatedWindowOpen && !props.$selected ? 1 : 0};
            transition: height 0.5s ease-in-out, opacity 0.5s ease-in-out;
            position: absolute;
            right: 2px;
            border-radius: 2.5px;
            width: 3px;
            background-color: currentColor;
          }
        `}
      >
        {children}
      </Link>
    </Tooltip>
  );
};
